/**
 * One field DEPLOYMENT, at its own address (/deployments/<id>).
 *
 * The page a copied link points at, and the page Fieldpath links to: a
 * deployment is coordinated between people who are not looking at the same
 * screen, and "the Buffalograss trip" pasted into a chat has to open something.
 * Its id never changes, so neither does the address.
 *
 * Since 21 Oct 2026 the deployment is its own record (Jeremy), changed HERE.
 * Since 27 Oct 2026 it is booked when saved, nobody confirms it, and it has a PM
 * chosen on the form: the creator, that PM and HR edit and cancel it; those
 * three and the crew add notes. Every change is in the history. The notes are read only by the people the private-details rule
 * names (Jeremy, 22 Oct 2026); everyone else gets null from the API and a
 * sentence here saying who can. What the caller may do comes from the API (`rights`), so a button
 * shown and a refusal given cannot disagree.
 */
import { mdy, mdyRange, mdyTime, type DeploymentDetail, type DeploymentEventView, type DeploymentSaved } from "@scheduler/shared";
import { useCallback, useEffect, useState, type FormEvent } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { fieldpathUrl } from "../links";
import { navigate, shareUrl } from "../router";
import { DeploymentForm } from "./DeploymentForm";
import { takeFlash, toldText } from "./flash";
import { StatusPill } from "./StatusPill";

export function Deployment({ id }: { id: string }) {
  const api = useSchedulerApi();
  const [deployment, setDeployment] = useState<DeploymentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDeployment(await api.deployment(id));
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof ApiError && e.status === 404
          ? "That deployment does not exist."
          : e instanceof ApiError ? e.message : String(e),
      );
    }
  }, [api, id]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    void load();
  }, [load, back]);

  // What the create that led here queued, carried across the navigation (FieldWork).
  const [message, setMessage] = useState<string | null>(() => takeFlash(id));

  const act = async (work: () => Promise<DeploymentSaved>): Promise<void> => {
    setBusy(true);
    setProblem(null);
    setMessage(null);
    try {
      const saved = await work();
      setDeployment(saved);
      setMessage(toldText(saved));
      setCancelling(false);
      setReason("");
    } catch (e: unknown) {
      setProblem(e instanceof ApiError ? e.message : String(e));
      // A refusal may mean it changed under us: show what is there now.
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <>
        <button type="button" className="ghost" onClick={() => navigate({ kind: "field" })}>← All field work</button>
        <div className="error" style={{ marginTop: 12 }}>{error}</div>
      </>
    );
  }
  if (!deployment) return <p className="muted">Loading…</p>;

  const d = deployment;
  const days = Math.round((Date.parse(`${d.end}T00:00:00Z`) - Date.parse(`${d.start}T00:00:00Z`)) / 86_400_000) + 1;

  return (
    <>
      <div className="deploybar">
        <button type="button" className="ghost" onClick={() => navigate({ kind: "field" })}>← All field work</button>
        <span className="deployactions">
          {/* Fieldpath finds the task linked to this deployment, or says none is yet. */}
          <a className="ghost-link" href={`${fieldpathUrl}/mobilizations/${id}`} target="_blank" rel="noreferrer">
            Open the task<span aria-hidden="true"> ↗</span>
          </a>
          <CopyLink url={shareUrl({ kind: "deployment", id })} />
        </span>
      </div>

      {editing ? (
        <DeploymentForm
          editing={d}
          onCancel={() => setEditing(false)}
          onSaved={(saved) => { setDeployment(saved); setEditing(false); setProblem(null); setMessage(toldText(saved)); }}
        />
      ) : (
        <div className="card card-pad">
          <div className="deployhead">
            <h2>{d.title}</h2>
            <StatusPill status={d.status} />
          </div>

          <dl className="deploymeta">
            <dt>When</dt>
            <dd>
              {mdyRange(d.start, d.end)}{" "}
              <span className="muted">({days} day{days === 1 ? "" : "s"})</span>
            </dd>

            {d.site ? (<><dt>Site</dt><dd>{d.site}</dd></>) : null}

            <dt>Crew</dt>
            <dd>{d.crew.length === 0 ? "Nobody on it" : d.crew.map((p) => p.name).join(", ")}</dd>

            <dt>Job</dt>
            <dd>{d.project ? d.project.name : <span className="muted">None (booked before jobs were required)</span>}</dd>

            <dt>PM</dt>
            <dd>{d.pm ? d.pm.name : <span className="muted">None chosen</span>}</dd>

            <dt>Created by</dt>
            <dd>{d.createdBy.name}</dd>

            {/* Null for anyone the API decided may not read it. */}
            {d.explanation ? (<><dt>Explanation</dt><dd className="explain">{d.explanation}</dd></>) : null}

            {d.cancelledBy ? (
              <>
                <dt>Cancelled</dt>
                <dd>{d.cancelledBy.name}{d.cancelReason ? `: ${d.cancelReason}` : ""}</dd>
              </>
            ) : null}

            {/* The id Fieldpath links to. Shown because when a field task there says it is linked
                to a deployment, this is the value it holds -- and seeing both ends is the
                difference between checking a link and believing in it. */}
            <dt>Deployment id</dt>
            <dd><code className="mobid">{d.id}</code><span className="muted"> — what the linked task holds</span></dd>
          </dl>

          {message ? <p className="toast" role="status">{message}</p> : null}
          {problem ? <p className="error" role="alert">{problem}</p> : null}

          <div className="deployactions" style={{ marginTop: 14 }}>
            {d.rights.edit ? (
              <button type="button" className="ghost" disabled={busy} onClick={() => setEditing(true)}>Change dates, crew or details</button>
            ) : null}
            {d.rights.cancel && !cancelling ? (
              <button type="button" className="ghost" disabled={busy} onClick={() => setCancelling(true)}>Cancel deployment</button>
            ) : null}
          </div>

          {cancelling ? (
            <form className="field" style={{ marginTop: 12 }}
              onSubmit={(e: FormEvent) => { e.preventDefault(); void act(() => api.cancelDeployment(d.id, reason)); }}>
              <span className="label">Why is it cancelled? The crew will see this.</span>
              <textarea value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} />
              <div className="deployactions" style={{ marginTop: 8 }}>
                <button type="submit" className="primary narrow" disabled={busy || !reason.trim()}>Cancel the whole trip</button>
                <button type="button" className="ghost" disabled={busy} onClick={() => { setCancelling(false); setReason(""); }}>Keep it</button>
              </div>
            </form>
          ) : null}
        </div>
      )}

      <Notes deployment={d} onAdded={setDeployment} />
      <History deployment={d} />
    </>
  );
}

function Notes({ deployment, onAdded }: { deployment: DeploymentDetail; onAdded: (d: DeploymentDetail) => void }) {
  const api = useSchedulerApi();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onAdded(await api.addDeploymentNote(deployment.id, body));
      setBody("");
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad" style={{ marginTop: 14 }} aria-labelledby="deploy-notes">
      <h3 id="deploy-notes" style={{ marginTop: 0 }}>Notes</h3>
      {/* null is "not yours to read", never "none": the API keeps them back (22 Oct 2026). */}
      {deployment.notes === null ? (
        <p className="muted">Notes are visible to the crew, the creator, the project's PM, other PMs and HR.</p>
      ) : deployment.notes.length === 0 ? <p className="muted">No notes yet.</p> : (
        <ul className="deploynotes">
          {deployment.notes.map((n) => (
            <li key={n.id}>
              <span className="muted">{n.author.name}, {mdyTime(n.at)}</span>
              <span className="explain">{n.body}</span>
            </li>
          ))}
        </ul>
      )}
      {deployment.rights.note ? (
        <form onSubmit={(e) => void add(e)}>
          <textarea value={body} maxLength={4000} rows={2} placeholder="Gate codes, access, who to call…" onChange={(e) => setBody(e.target.value)} />
          {error ? <p className="error" role="alert">{error}</p> : null}
          <button type="submit" className="ghost" disabled={busy || !body.trim()} style={{ marginTop: 6 }}>{busy ? "Adding…" : "Add note"}</button>
        </form>
      ) : null}
    </section>
  );
}

const FIELD_NAME: Record<string, string> = { title: "name", start: "first day", end: "last day", site: "site", crew: "crew", explanation: "explanation", pm: "PM" };

/** One history line, in words. Crew ids are named from the crew now, or by number if they left. */
function describe(e: DeploymentEventView, names: ReadonlyMap<string, string>): string {
  if (e.kind === "CREATED") return e.detail["migratedFromRequests"] ? "Created (from the field booking it was before 21 Oct 2026)" : "Booked";
  // A PM's or HR's sign-off from before 27 Oct 2026, when field work still had one. Shown as what
  // it means now, never as an approval.
  if (e.kind === "CONFIRMED") return "Booked";
  if (e.kind === "BOOKED") return "Booked when field work stopped needing a sign-off (27 Oct 2026)";
  if (e.kind === "CANCELLED") return `Cancelled${typeof e.detail["reason"] === "string" ? `: ${e.detail["reason"]}` : ""}`;
  const changes = (e.detail["changes"] ?? {}) as Record<string, { from: unknown; to: unknown }>;
  const show = (field: string, v: unknown): string =>
    v === null ? "none" : field === "crew" && Array.isArray(v) ? v.map((id) => names.get(String(id)) ?? `#${id}`).join(", ")
      : field === "start" || field === "end" ? mdy(String(v)) : String(v);
  const parts = Object.entries(changes).map(([field, c]) =>
    field === "explanation" ? "explanation changed" : `${FIELD_NAME[field] ?? field} ${show(field, c.from)} → ${show(field, c.to)}`);
  return `Changed ${parts.join("; ")}`;
}

function History({ deployment }: { deployment: DeploymentDetail }) {
  const names = new Map(deployment.crew.map((p) => [p.id, p.name]));
  return (
    <section className="card card-pad" style={{ marginTop: 14 }} aria-labelledby="deploy-history">
      <h3 id="deploy-history" style={{ marginTop: 0 }}>History</h3>
      <ul className="deployhistory">
        {deployment.history.map((e) => (
          <li key={e.id}>
            {/* The migration's row has the creator as its actor only because one is required. */}
            <span className="muted">{mdyTime(e.at)}{e.kind === "BOOKED" ? "" : ` · ${e.actor.name}`}</span>
            <span>{describe(e, names)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * /field/<request id>: a link pasted before 21 Oct 2026, when field work was a request. Looked up
 * and REPLACED by the deployment it became, so Back does not bounce through it.
 */
export function LegacyFieldLink({ requestId }: { requestId: string }) {
  const api = useSchedulerApi();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.deploymentForRequest(requestId).then(
      (deploymentId) => navigate({ kind: "deployment", id: deploymentId }, true),
      () => setError("That field booking does not exist."),
    );
  }, [api, requestId]);
  return error ? (
    <>
      <button type="button" className="ghost" onClick={() => navigate({ kind: "field" })}>← All field work</button>
      <div className="error" style={{ marginTop: 12 }}>{error}</div>
    </>
  ) : <p className="muted">Finding that deployment…</p>;
}

/**
 * `navigator.clipboard` needs a secure context and permission, and refuses in
 * places this actually runs, so there is a fallback rather than a button that
 * silently does nothing.
 */
function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const field = document.createElement("textarea");
      field.value = url;
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.appendChild(field);
      field.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(field);
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button type="button" className="ghost" onClick={() => void copy()} title={url}>
      {copied ? "✓ Link copied" : "Copy link"}
    </button>
  );
}
